import type { Reference } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { cardEditContext } from '@context/card-edit-context';
import { CardFieldDetailsFragmentDoc } from '@graphql/fragments/cards/field.graphql';
import {
  CreateCardFieldDocument,
  RemoveCardFieldDocument,
  UpdateCardFieldDocument,
} from '@graphql/mutations/cards/field.graphql';
import { GetCardFieldLexoranksDocument, type GetCardFieldLexoranksQuery } from '@hooks/card/query.graphql';
import { formatUrl } from '@modules/helpers';
import { createPhoneNumberLink } from '@modules/url-helpers';
import { apolloClient } from '@services/apollo';
import { cardClaimContext } from 'app-next/claim/card/contexts';
import { LexoRank } from 'lexorank';
import { useContext } from 'react';

import type {
  CardAttributeType,
  CardField as CardFieldType,
  CreateCardFieldInput,
  RemoveCardFieldInput,
  UpdateCardFieldInput,
} from '.cache/__types__';

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

export const getNewCardFieldLexorank = (cardId: string): string => {
  // Get card fields lexoranks
  const response = apolloClient.readQuery<GetCardFieldLexoranksQuery>({
    query: GetCardFieldLexoranksDocument,
    variables: {
      cardId,
    },
  });

  // If no prior fields created, return middle lexorank value
  if (!response?.card?.fields || response?.card?.fields?.length === 0) {
    return LexoRank.middle().format();
  }

  // get the highest lexorank value
  const sortedLexoranks = [...response.card.fields]
    .map((item) => item.lexorank)
    .filter(notEmpty)
    .sort((a, b) => a.localeCompare(b));

  const highestLexorankValue = sortedLexoranks[sortedLexoranks.length - 1];
  if (!highestLexorankValue) {
    return LexoRank.middle().format();
  }

  // return the next lexorank value after current highest card field lexorank
  return LexoRank.parse(highestLexorankValue).genNext().format();
};

export const useCardFieldMutations = (cardId: string, field: CardFieldType) => {
  const [createCardFieldMutation] = useMutation(CreateCardFieldDocument);
  const [updateCardFieldMutation] = useMutation(UpdateCardFieldDocument);
  const [removeCardFieldMutation] = useMutation(RemoveCardFieldDocument);

  const { setPendingCardField } = useContext(cardEditContext);

  const { claimCardToken } = useContext(cardClaimContext);

  /* 
    If we're running mutations during the card claim flow,
    then we need to override the authorization headers to use the
    Claim Card Token.
  */
  const mutationContext = claimCardToken
    ? {
        headers: {
          authorization: `ClaimCard ${claimCardToken}`,
        },
      }
    : undefined;

  const handleCreate = (input: CreateCardFieldInput, activeAttribute?: CardAttributeType) => {
    createCardFieldMutation({
      context: mutationContext,
      variables: {
        input: {
          ...input,
        },
      },
      optimisticResponse: {
        createCardField: {
          __typename: 'CreateCardFieldPayload',
          field: {
            __typename: 'CardField',
            id: 'temp-id',
            value: input.value,
            label: input.label,
            attribute_type: activeAttribute,
            card_id: input.card_id,
            lexorank: input.lexorank,
            shorthand: input.shorthand ?? null,
            stage: 'todo',
            is_optional: false,
            value_locked: false,
            instruction_text: null,
            label_locked: false,
            phone_country_code: null,
            phone_extension: null,
            template_id: null,
            value_provider: input.value_provider,
            template: null,
          },
        },
      },
      /* 
        Note: 'update' gets called twice. Once with the optimisticResponse (above)
        then again with the actual network response
      */
      update: (cache, { data }) => {
        const newField = data?.createCardField;
        if (!newField || !cardId) return;

        const newCardFieldRef = cache.writeFragment({
          // We must prefix this id with the string 'CardField:' otherwise the resulting cache ref
          // won't actually point to the right place
          id: `CardField:${data.createCardField?.field?.id}`,
          data: data.createCardField?.field,
          fragment: CardFieldDetailsFragmentDoc,
          fragmentName: 'CardFieldDetails',
        });

        cache.modify({
          id: cache.identify({ __typename: 'Card', id: cardId }),
          fields: {
            fields(existingCardFieldRefs = []) {
              return [...existingCardFieldRefs, newCardFieldRef];
            },
          },
        });

        /* 
          Drop the pending card field from context.
          This gets replaced with an actual card field from the Apollo cache.
        */
        setPendingCardField(undefined);
      },
    });
  };

  const handleUpdate = (input: UpdateCardFieldInput) => {
    updateCardFieldMutation({
      context: mutationContext,
      variables: {
        input,
      },
    });
  };

  const handleRemove = (input: RemoveCardFieldInput) => {
    if (!field?.id) return;
    removeCardFieldMutation({
      context: mutationContext,
      variables: {
        input: input,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        removeCardField: {
          __typename: 'RemoveCardFieldPayload',
          id: field.id,
        },
      },
      update: (cache, { data }) => {
        cache.modify({
          id: cache.identify({ __typename: 'Card', id: cardId }),
          fields: {
            fields(existingFields: readonly Reference[]) {
              // Remove the deleted field from the cache via the card object.
              const updatedFields = existingFields.filter(
                (field) => field.__ref !== `CardField:${data?.removeCardField?.id}`,
              );
              return updatedFields;
            },
          },
        });

        // This cleans up the de-referenced card field from the cache
        cache.gc();
      },
    });
  };

  return {
    create: handleCreate,
    update: handleUpdate,
    remove: handleRemove,
  };
};

export const getHref = (field: CardFieldType): string => {
  switch (field.attribute_type?.name) {
    case 'email': {
      return `mailto:${field.value}`;
    }
    case 'phone': {
      return createPhoneNumberLink(field.value ?? '', field.phone_extension ?? '');
    }
    case 'address': {
      return `https://maps.google.com/?q=${field.value}`;
    }
    case 'url':
    default: {
      return formatUrl(field?.attribute_type?.name ?? '', field.value ?? '');
    }
  }
};
